import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  Eye, 
  EyeOff, 
  Loader, 
  AlertCircle, 
  Lock,
  User
} from 'lucide-react';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
}

interface AdminLoginProps {
  onLogin: (userData: User) => void;
}

interface LoginState {
  username: string;
  password: string;
  showPassword: boolean;
  isLoading: boolean;
  error: string | null;
  remainingAttempts: number | null;
  lockoutTime: number | null;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin }) => {
  const [state, setState] = useState<LoginState>({
    username: '',
    password: '',
    showPassword: false,
    isLoading: false,
    error: null,
    remainingAttempts: null,
    lockoutTime: null
  });

  const [csrfToken, setCsrfToken] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(0);

  // Fetch CSRF token on component mount
  useEffect(() => {
    fetchCsrfToken();
  }, []);

  // Handle lockout countdown
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setState(prev => ({ ...prev, lockoutTime: null, error: null }));
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [countdown]);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch('/api/csrf-token', {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCsrfToken(data.csrf_token);
      }
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
    }
  };

  const handleInputChange = (field: keyof LoginState, value: string | boolean) => {
    setState(prev => ({
      ...prev,
      [field]: value,
      error: null // Clear errors when user starts typing
    }));
  };

  const validateForm = (): boolean => {
    if (!state.username.trim()) {
      setState(prev => ({ ...prev, error: 'Username is required' }));
      return false;
    }
    
    if (!state.password) {
      setState(prev => ({ ...prev, error: 'Password is required' }));
      return false;
    }

    if (state.password.length < 8) {
      setState(prev => ({ ...prev, error: 'Invalid credentials' }));
      return false;
    }

    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (state.lockoutTime && Date.now() < state.lockoutTime) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: state.username.trim(),
          password: state.password,
          csrf_token: csrfToken
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Successful login
        setState(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: null,
          remainingAttempts: null,
          lockoutTime: null
        }));
        
        onLogin(data.user);
      } else {
        // Login failed
        let errorMessage = data.error || 'Login failed';
        let lockoutTime = null;
        let remainingAttempts = null;

        // Handle rate limiting
        if (response.status === 429) {
          errorMessage = 'Too many login attempts. Please try again later.';
          lockoutTime = Date.now() + (15 * 60 * 1000); // 15 minutes
          setCountdown(15 * 60);
        }

        // Handle account lockout
        if (errorMessage.includes('locked')) {
          lockoutTime = Date.now() + (30 * 60 * 1000); // 30 minutes
          setCountdown(30 * 60);
        }

        // Extract remaining attempts from error message
        const attemptsMatch = errorMessage.match(/(\d+)\/\d+/);
        if (attemptsMatch) {
          const attempts = parseInt(attemptsMatch[1]);
          remainingAttempts = 5 - attempts;
        }

        setState(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
          remainingAttempts,
          lockoutTime,
          password: '' // Clear password on failure
        }));

        // Refresh CSRF token after failed attempt
        await fetchCsrfToken();
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Network error. Please check your connection.',
        password: ''
      }));

      // Refresh CSRF token after network error
      await fetchCsrfToken();
    }
  };

  const formatCountdown = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const isFormDisabled = state.isLoading || (state.lockoutTime && Date.now() < state.lockoutTime);

  return (
    <>
      <style>
        {`
          @keyframes gradient-slide {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 bg-[#0a0a0a] backdrop-blur-sm rounded-xl p-8 border border-white/[0.08] w-full max-w-md mx-4"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="flex items-center justify-center mx-auto mb-4"
          >
            <img
              src="/images/cipher_character_notext.png"
              alt="CIPHER Security Logo"
              className="w-16 h-16 object-contain"
              style={{
                filter: "brightness(1.1) contrast(1.1)"
              }}
            />
          </motion.div>
          
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-white mb-2">CIPHER Security</h1>
            <span 
              className="text-sm font-medium inline-block bg-gradient-to-r from-[#00a86b] via-[#06b6d4] via-[#fbbf24] via-[#06b6d4] to-[#00a86b] bg-clip-text text-transparent animate-pulse"
              style={{
                backgroundSize: '300% 100%',
                animation: 'gradient-slide 3s linear infinite'
              }}
            >
              Prevent&nbsp;•&nbsp;Detect&nbsp;•&nbsp;Respond
            </span>
          </div>
        </div>

        {/* Lockout Warning */}
        <AnimatePresence>
          {countdown > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 p-4 bg-orange-500/20 border border-orange-500/40 rounded-lg"
            >
              <div className="flex items-center text-orange-200">
                <Lock className="w-5 h-5 mr-2" />
                <div>
                  <p className="font-semibold">Account Temporarily Locked</p>
                  <p className="text-sm">Please wait {formatCountdown(countdown)} before trying again</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          {/* Username Field */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
              Username or Email
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="username"
                type="text"
                value={state.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                disabled={isFormDisabled}
                className="w-full pl-10 pr-4 py-3 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15] disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter your username or email"
                autoComplete="username"
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="password"
                type={state.showPassword ? 'text' : 'password'}
                value={state.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                disabled={isFormDisabled}
                className="w-full pl-10 pr-12 py-3 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15] disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => handleInputChange('showPassword', !state.showPassword)}
                disabled={isFormDisabled}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {state.showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Security Info */}
          {state.remainingAttempts !== null && state.remainingAttempts <= 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-yellow-300 bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-3"
            >
              <AlertCircle className="w-4 h-4 inline mr-2" />
              {state.remainingAttempts > 0
                ? `${state.remainingAttempts} attempt${state.remainingAttempts !== 1 ? 's' : ''} remaining`
                : 'Account will be locked after next failed attempt'
              }
            </motion.div>
          )}

          {/* Error Message */}
          <AnimatePresence mode="wait">
            {state.error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center p-3 bg-red-500/20 border border-red-500/40 rounded-lg text-red-200 text-sm"
              >
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                <span>{state.error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Login Button */}
          <button
            type="submit"
            disabled={isFormDisabled || !csrfToken}
            className="w-full py-3 px-4 bg-gradient-to-r from-[#00a86b] to-[#06b6d4] hover:from-[#00c77d] hover:to-[#0891b2] text-white font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {state.isLoading ? (
              <>
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                Authenticating...
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>
      </motion.div>
      </div>
    </>
  );
};

export default AdminLogin;